import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { debounceTime } from 'rxjs/operators';
import { formConstant } from '../../../../../config';
import { ICountry } from '../../../../core/models/country.interface';
import { PageParams } from '../../../../core/models/page-params.model';
import { IPaginatorProperties } from '../../../../core/models/paginator.interface';
import { ConstantService } from '../../../../core/services/constant.service';
import { NotificationService } from '../../../../core/services/notification.service';
import { UserService } from '../../../../core/services/user.service';
import { AssignVendorPopupComponent } from '../../../../shared/popups/assign-vendor-popup/assign-vendor-popup.component';
import { ReasonPopupComponent } from '../../../../shared/popups/reason-popup/reason-popup.component';
import { IService, IStatus, ISubService, IVendor } from '../../models/shipment-price.interface';
import { ShipmentPriceService } from '../../services/shipment-price.service';
import { VendorComponent } from './vendor/vendor.component';

@Component({
    selector: 'eci-vendors',
    templateUrl: './vendors.component.html',
    styleUrls: ['./vendors.component.scss']
})
export class VendorsComponent implements OnInit {

    @Input() public isService = false;
    @Input() public serviceId;

    @Output() public pageChange: EventEmitter<PageParams> = new EventEmitter<PageParams>(undefined);
    @Output() onNavigate: EventEmitter<any> = new EventEmitter<any>(null);

    public pageParams: PageParams = new PageParams();
    public currentSort: Sort;
    public displayedColumns = ['vendorId', 'vendorName'];
    public showLoader: boolean = false;
    public dataSource: MatTableDataSource<IVendor> = new MatTableDataSource<IVendor>([]);
    public countries: ICountry[] = [];
    public services: IService[] = [];
    public form: FormGroup;
    private vendors: IVendor[] = [];

    constructor(private userSvc: UserService,
                private dialog: MatDialog,
                private notify: NotificationService,
                private constSvc: ConstantService,
                private shipmentPriceSvc: ShipmentPriceService,
    ) {

    }

    ngOnInit(): void {
        this.setTableColumn();
        this.form = this.initializeForm();
        this.searchSubscriber();
        this.getListOfCountries();
        this.getParams<IService>('service', 'services', {});
        this.getVendors();
    }

    private initializeForm(): FormGroup {
        this.pageParams.pageSize = formConstant.pageSize;
        this.pageParams.pageIndex = 0;
        return new FormGroup({
            search: new FormControl(''),
            vendor: new FormControl(),
        });

    }

    private setTableColumn(): void {
        const columnMappings = {
            3: ['disableReason', 'disabledBy', 'createdBy', 'active'],
            2: [],
            1: ['country', 'contactName', 'contactTel', 'contactEmail', 'serviceIds', 'createdBy', 'active', 'edit', 'delete'],
            0: ['country', 'contactName', 'contactTel', 'contactEmail', 'serviceIds'],
        };
        const key: number = (Number(this.isService) << 1) | Number(this.isAdmin); // Convert booleans to numbers
        this.displayedColumns.push(...columnMappings[key]);
    }

    private searchSubscriber(): void {
        this.form.get('search').valueChanges.pipe(debounceTime(500))
            .subscribe((value) => {
                this.getVendors();
            });
    }

    private getVendors(
        page: number = this.pageParams.pageIndex + 1 || 1,
        limit: number = this.pageParams.pageSize,
        q: string = this.form?.get('search')?.value || '',
        orderField: string = this.currentSort?.active || '',
        orderDir: string = this.currentSort?.direction.toUpperCase() || ''): void {
        let filter = {};
        const path = this.isService ? 'vendor/vendor-service' : 'vendor';
        if (this.isService) {
            filter = {serviceId: this.serviceId};
        }
        this.showLoader = true;
        this.shipmentPriceSvc.get<IPaginatorProperties<IVendor>>(path, page, limit, q, filter, orderField, orderDir)
            .subscribe((data) => {
                if (!data) {
                    return;
                }
                let items = data?.items;
                if (this.serviceId) {
                    items = data?.items.map((i) => {
                        const {active, ...rest} = i?.vendor;
                        delete i.vendor;
                        return {...i, isDisabled: !active, ...rest};
                    });
                }
                this.dataSource = new MatTableDataSource<IVendor>(items);
                this.vendors = items;
                this.pageParams.pageSize = data.meta.itemsPerPage;
                this.pageParams.pageIndex = data.meta.currentPage - 1;
                this.pageParams.length = data.meta.totalItems;
            }, (err) => {
                this.notify.showError(err.error.message);
            }, () => {
                this.showLoader = false;
            });
    }

    private getParams<T>(path: string, objectName: string,
                         filter: any = {},
                         q: string = '',
                         orderField: string = 'name',
                         orderDirection: string = 'ASC',
                         page: number = 1,
                         limit: number = 1000): void {
        this.shipmentPriceSvc.get<IPaginatorProperties<T>>(path, page, limit, q, filter, orderField, orderDirection)
          .subscribe((data) => {
              if (!data) {
                  return;
              }
              this[objectName] = data.items;
          });
    }

    private getListOfCountries(
        page: number = 1,
        q: string = '',
        limit: number = 1000,
        withTaxRate: boolean = false,
    ): void {
        this.constSvc.getAvailableCountriesForScroll(page, limit, false, q, withTaxRate).subscribe(data => {
            if (data) {
                this.countries = data.items;
            }
        });
    }

    public get isAdmin(): boolean {
        return this.userSvc.isAdmin;
    }

    public openDialog(vendorDetail?: any): void {
        const createMode = !vendorDetail;
        const dialogRef = this.dialog.open(VendorComponent,
            {
                maxHeight: '800px',
                width: createMode ? '640px' : '900px',
            },
        );
        dialogRef.componentInstance.createMode = createMode;
        dialogRef.componentInstance.vendor = !vendorDetail ? undefined : vendorDetail;
        dialogRef.componentInstance.title = !vendorDetail ? 'Add Vendor' : 'Edit Vendor';
        dialogRef.componentInstance.countries = this.countries;
        dialogRef.componentInstance.services = this.isService ?
            this.services.filter((value) => value.id === this.serviceId) :
            this.services;
        dialogRef.afterClosed()
            .subscribe((vendor) => {
                if (!vendor) {
                    return;
                }
                this.getVendors();
            });
    }

    public onStatusChange(event: IStatus): void {
        if (!event.status) {
            event.disabledBy = this.userSvc.user.email;
            event.reason = 'Disabled from system vendor';
        }
        if (this.isService && !event.status) {
            const dialogRef = this.dialog.open(ReasonPopupComponent, {width: '400px'});
            dialogRef.afterClosed().subscribe((value) => {
                if (!value) {
                    this.getVendors();
                    return;
                }
                event.reason = value;
                this.updateStatus(event);
            });

        } else {
            this.updateStatus(event);
        }
    }

    private updateStatus(event: IStatus): void {
        this.showLoader = true;
        this.shipmentPriceSvc.updateVendorStatus(this.isService ? 'service-vendor/status' : 'status', event).subscribe((v) => {
            this.notify.showSuccessMessage(`Update successfully`);
            this.getVendors();
        }, (err) => {
            this.notify.showError(err.error.message);
        }, () => {
            this.showLoader = false;
        });
    }

    public assignVendor(): void {
        const dialogRef = this.dialog.open(AssignVendorPopupComponent);
        dialogRef.componentInstance.selectedVendor = this.vendors;
        dialogRef.afterClosed()
            .subscribe((vendor) => {
                if (!vendor) {
                    return;
                }
                this.shipmentPriceSvc.assignVendor(this.serviceId, vendor).subscribe((v) => {
                    this.notify.showSuccessMessage(`Update successfully`);
                    this.getVendors();
                }, (err) => {
                    this.notify.showError(err.error.message);
                }, () => {
                    this.showLoader = false;
                });
            });
    }

    public pageChanged(e: PageParams): void {
        this.pageParams.pageIndex = e.pageIndex;
        this.pageParams.pageSize = e.pageSize;
        this.getVendors();
    }

    public sortData(sort: Sort): void {
        this.currentSort = sort;
        this.getVendors();
    }

    public onDelete(id: number): void {
        this.showLoader = true;
        this.shipmentPriceSvc.delete('vendor', id).subscribe((v) => {
            this.notify.showSuccessMessage(`Delete successfully`);
            this.getVendors();
        }, (err) => {
            this.notify.showError(err.error.message);
        }, () => {
            this.showLoader = false;
        });
    }
}
